/**
 * useAiQuota - React hook for the user's real AI quota.
 *
 * Fetches GET /v1/ai/quota (served by the user's own Durable Object): the plan is derived there from the
 * licence, the numbers are the messages used and the limit this month (what to show is decided in
 * aiQuotaDisplay.ts). It used to ask an edge stub that answered "0 of 500" for everyone.
 */

import { useState, useEffect, useCallback } from "react"
import { apiRequest } from "@/shared/utils/api/apiClient"
import { quotaView, type QuotaStatus, type QuotaView } from "./aiQuotaDisplay"

export type { QuotaStatus }

export interface AiQuotaState {
  /** Current quota data (null until first fetch) */
  quota: QuotaStatus | null
  /** What to show for it (null until first fetch) */
  view: QuotaView | null
  /** Loading state */
  loading: boolean
  /** Error message if fetch failed */
  error: string | null
  /** Whether quota is at warning level (>80% consumed) */
  isWarning: boolean
  /** Whether the user cannot send: the month's quota is used up, or the plan has no AI */
  isExhausted: boolean
  /** Whether the plan has no AI at all (free, expired trial) */
  noPlan: boolean
  /** Manually refetch quota */
  refetch: () => void
}

export function useAiQuota(): AiQuotaState {
  const [quota, setQuota] = useState<QuotaStatus | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchQuota = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const res = await apiRequest<QuotaStatus>({ path: "/v1/ai/quota" })

      if (!res.ok) {
        if (res.status === 401) {
          setError("Authentication expired")
        } else {
          setError(`Failed to fetch quota (${res.status})`)
        }
        return
      }

      setQuota(res.data as QuotaStatus)
    } catch (e) {
      setError(e instanceof Error ? e.message : "Network error")
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void fetchQuota()
  }, [fetchQuota])

  const view = quota ? quotaView(quota) : null

  return {
    quota,
    view,
    loading,
    error,
    isWarning: view?.state === "warning",
    isExhausted: view?.state === "exhausted" || view?.state === "no_plan",
    noPlan: view?.state === "no_plan",
    refetch: fetchQuota,
  }
}
